import { useMemo } from 'react'
import type { EnhancedProductFamilyId, EnhancedProductGroupId } from '#api/enhancements/enhancedTmsApiTypes'
import { fail } from '#app/assert'
import type { ConfigurationEnrichmentData } from '#app/store/slices/enrichConfiguration'
import type { AppProductGroup, AppProductVariant, ProductPath } from '#app/types/AppTypes'

interface AddedChangeRecord {
  variant: AppProductVariant
  change: 'added'
  previous?: AppProductVariant
}

interface RemovedChangeRecord {
  variant: AppProductVariant
  change: 'removed'
  previous?: AppProductVariant
}

interface SelectedChangeRecord {
  variant: AppProductVariant
  change: 'selected'
  previous?: AppProductVariant
}

interface DeselectedChangeRecord {
  variant: AppProductVariant
  change: 'deselected'
  to?: AppProductVariant
}

interface UnchangedChangeRecord {
  variant: AppProductVariant
  change: 'unchanged'
}

interface PriceChangeRecord {
  variant: AppProductVariant
  change: 'priceChanged'
  delta: number
}

export type ChangeRecord =
  | AddedChangeRecord
  | RemovedChangeRecord
  | SelectedChangeRecord
  | DeselectedChangeRecord
  | UnchangedChangeRecord
  | PriceChangeRecord

type DiffCollection<T extends ChangeRecord = ChangeRecord> = Record<ProductPath, T>

export interface ConfigurationDiff {
  added: DiffCollection<AddedChangeRecord>
  removed: DiffCollection<RemovedChangeRecord>
  selected: DiffCollection<SelectedChangeRecord>
  deselected: DiffCollection<DeselectedChangeRecord>
  unchanged: DiffCollection<UnchangedChangeRecord>
  priceChanged: DiffCollection<PriceChangeRecord>
  byPath: DiffCollection
  /** Group IDs that have any form of change */
  affectedGroups: Set<EnhancedProductGroupId>
  /** Family IDs that have any form of change */
  affectedFamilies: Set<EnhancedProductFamilyId>
  /** Group IDs that contain variants that are missing in one of the configurations */
  missingGroups: Set<EnhancedProductGroupId>
  /** Family IDs that contain variants that are missing in one of the configurations */
  missingFamilies: Set<EnhancedProductFamilyId>
  priceChangeGroups: Set<EnhancedProductGroupId>
  priceChangeFamilies: Set<EnhancedProductFamilyId>
}

const allParentGroups = (productGroup: AppProductGroup | undefined): AppProductGroup[] =>
  productGroup ? [...allParentGroups(productGroup?.parentGroup), productGroup] : []

const isNoSelectionVariant = (variant: AppProductVariant) => variant.id.endsWith('-')

export const useConfigurationDifference = (
  baseEnrichment: ConfigurationEnrichmentData | undefined,
  comparisonEnrichment: ConfigurationEnrichmentData | undefined,
  includeRemovalsAndAdditions?: boolean,
) => {
  return useMemo(() => {
    const diff: ConfigurationDiff = {
      added: {},
      removed: {},
      selected: {},
      deselected: {},
      unchanged: {},
      priceChanged: {},
      byPath: {},
      affectedGroups: new Set(),
      affectedFamilies: new Set(),
      missingGroups: new Set(),
      missingFamilies: new Set(),
      priceChangeGroups: new Set(),
      priceChangeFamilies: new Set(),
    }

    if (!baseEnrichment && !comparisonEnrichment) {
      return diff
    }

    if (!baseEnrichment) {
      return diff
    }

    function markAffected(variant: AppProductVariant) {
      diff.affectedFamilies.add(variant.parentFamily.id)
      for (const parentGroup of allParentGroups(variant.parentFamily.parentGroup)) {
        diff.affectedGroups.add(parentGroup.id)
      }
    }

    function markMissing(variant: AppProductVariant) {
      diff.missingFamilies.add(variant.parentFamily.id)
      diff.missingGroups.add(variant.parentFamily.parentGroup.id)
      if (variant.parentFamily.parentGroup.parentGroup) {
        diff.missingGroups.add(variant.parentFamily.parentGroup.parentGroup.id)
      }
      if (includeRemovalsAndAdditions) {
        markAffected(variant)
      }
    }

    function markPriceChanged(variant: AppProductVariant) {
      diff.priceChangeFamilies.add(variant.parentFamily.id)
      diff.priceChangeGroups.add(variant.parentFamily.parentGroup.id)
      if (variant.parentFamily.parentGroup.parentGroup) {
        diff.priceChangeGroups.add(variant.parentFamily.parentGroup.parentGroup.id)
      }
    }

    const baseFamilyIds = new Set(Object.keys(baseEnrichment.enrichedFamilies).map((id) => id as EnhancedProductFamilyId))
    const comparisonFamilyIds = new Set(Object.keys(comparisonEnrichment?.enrichedFamilies ?? {}).map((id) => id as EnhancedProductFamilyId))

    const inBothFamilyIds = baseFamilyIds.intersection(comparisonFamilyIds)
    const onlyInBaseFamilyIds = baseFamilyIds.difference(comparisonFamilyIds)
    const onlyInComparisonFamilyIds = comparisonFamilyIds.difference(baseFamilyIds)

    for (const sharedFamilyId of inBothFamilyIds) {
      const baseFamily = baseEnrichment.enrichedFamilies[sharedFamilyId]
      const comparisonFamily = comparisonEnrichment?.enrichedFamilies[sharedFamilyId]

      const baseVariant = baseFamily?.selectedVariant
      const comparisonVariant = comparisonFamily?.selectedVariant

      if (!baseVariant && comparisonVariant) {
        const entry = {
          change: 'added',
          variant: comparisonVariant,
        } satisfies AddedChangeRecord
        diff.byPath[comparisonVariant.path] = entry
        diff.added[comparisonVariant.path] = entry
        markMissing(comparisonVariant)
        continue
      }

      if (!comparisonVariant && baseVariant) {
        const entry = {
          change: 'removed',
          variant: baseVariant,
        } satisfies RemovedChangeRecord
        diff.byPath[baseVariant.path] = entry
        diff.removed[baseVariant.path] = entry
        markMissing(baseVariant)
        continue
      }

      if (!baseVariant || !comparisonVariant) {
        fail('Both `baseVariant` and `comparisonVariant` was not found')
        continue
      }

      // both are selected or unselected
      if (baseVariant.id === comparisonVariant.id) {
        if (baseVariant.priceData?.price && comparisonVariant.priceData?.price && baseVariant.priceData.price !== comparisonVariant.priceData.price) {
          const delta = baseVariant.priceData.price - comparisonVariant.priceData.price
          diff.priceChanged[baseVariant.path] = {
            variant: baseVariant,
            change: 'priceChanged',
            delta,
          } satisfies PriceChangeRecord
          markPriceChanged(baseVariant)
          continue
        }

        const entry = { variant: baseVariant, change: 'unchanged' } satisfies UnchangedChangeRecord
        diff.byPath[baseVariant.path] = entry
        diff.unchanged[baseVariant.path] = entry
        continue
      }

      if (isNoSelectionVariant(baseVariant)) {
        const entry = {
          change: 'removed',
          variant: comparisonVariant,
        } satisfies RemovedChangeRecord
        diff.byPath[baseVariant.path] = entry
        diff.removed[baseVariant.path] = entry
        markMissing(baseVariant)
        continue
      }

      if (isNoSelectionVariant(comparisonVariant)) {
        const entry = {
          variant: baseVariant,
          change: 'added',
        } satisfies AddedChangeRecord
        diff.byPath[baseVariant.path] = entry
        diff.added[baseVariant.path] = entry
        markMissing(baseVariant)
        continue
      }

      const deselectedEntry = {
        variant: comparisonVariant,
        change: 'deselected',
        to: baseVariant,
      } satisfies DeselectedChangeRecord
      diff.byPath[comparisonVariant.path] = deselectedEntry
      diff.deselected[comparisonVariant.path] = deselectedEntry
      markAffected(comparisonVariant)

      const selectedEntry = {
        variant: baseVariant,
        change: 'selected',
        previous: comparisonVariant,
      } satisfies SelectedChangeRecord
      diff.byPath[baseVariant.path] = selectedEntry
      diff.selected[baseVariant.path] = selectedEntry
      markAffected(baseVariant)
    }

    for (const familyId of onlyInBaseFamilyIds) {
      const baseFamily = baseEnrichment.enrichedFamilies[familyId]
      const baseVariant = baseFamily?.selectedVariant
      if (!baseVariant) {
        fail('`baseVariant` was not found')
        continue
      }

      const entry = {
        variant: baseVariant,
        change: 'added',
      } satisfies AddedChangeRecord
      diff.byPath[baseVariant.path] = entry
      diff.added[baseVariant.path] = entry
      markMissing(baseVariant)
    }

    for (const familyId of onlyInComparisonFamilyIds) {
      const comparisonFamily = comparisonEnrichment?.enrichedFamilies[familyId]
      const comparisonVariant = comparisonFamily?.selectedVariant

      if (!comparisonVariant) {
        fail('`comparisonVariant` was not found')
        continue
      }

      const entry = {
        variant: comparisonVariant,
        change: 'removed',
      } satisfies RemovedChangeRecord
      diff.byPath[comparisonVariant.path] = entry
      diff.removed[comparisonVariant.path] = entry
      markMissing(comparisonVariant)
    }

    return diff
  }, [baseEnrichment, comparisonEnrichment, includeRemovalsAndAdditions])
}
